import { useQueryClient } from '@tanstack/react-query';
import type { MeDTO, Permission } from '@taskos/shared';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { setZone } from '../lib/format';
import { applyTheme } from '../lib/theme';
import { onUnauthorized, setCsrf } from './client';
import { useMe } from './queries';

interface Session {
  me: MeDTO;
  can: (p: Permission) => boolean;
}
const Ctx = createContext<Session | null>(null);

export function SessionProvider({ me, children }: { me: MeDTO; children: ReactNode }) {
  const value = useMemo(() => ({ me, can: (p: Permission) => me.permissions.includes(p) }), [me]);
  useEffect(() => {
    setCsrf(me.csrfToken);
    setZone(me.timezone);
    applyTheme(me.preferences.theme);
  }, [me]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('No session');
  return s;
}

/** Loads the session; on 401 anywhere, drops cached data and shows the login screen. */
export function useSessionLoader() {
  const me = useMe();
  const qc = useQueryClient();
  useEffect(
    () =>
      onUnauthorized(() => {
        // Drop everything except the session probe, then re-check it once (quietly): a 401 there
        // switches the app to the login screen; a success means it was a one-off.
        setCsrf(null);
        qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
        void qc.invalidateQueries({ queryKey: ['me'] });
      }),
    [qc],
  );
  return me;
}
